<template>
  <div class="pa-4 pt-8">
    <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <div v-if="!loading && appointments.length === 0" class="text-center pa-8">
        <v-icon size="48" color="grey">mdi-calendar-blank</v-icon>
        <div class="text-body-1 mt-2">No appointment history</div>
      </div>

      <v-list v-if="!loading && appointments.length > 0">
        <v-list-item
          v-for="appointment in appointments"
          :key="appointment.id"
          class="appointment-item mb-2 border"
        >
          <template #prepend>
            <v-icon color="primary">mdi-calendar</v-icon>
          </template>
          <v-list-item-title class="font-weight-bold">
            {{ formatAppointmentDate(appointment.datetime) }}
          </v-list-item-title>
          <v-list-item-subtitle>
            <div class="mt-1 d-flex align-center flex-wrap ga-2">
              <v-chip
                v-for="service in getServiceObjects(appointment)"
                :key="service.id"
                size="small"
                variant="flat"
                :class="`bg-${getTitleColor(service)}`"
              >
                {{ service.description }}
              </v-chip>
            </div>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="appointment.stylist">
            <div>
              <v-icon size="small" class="mr-1">mdi-account</v-icon>
              <span class="font-weight-medium">Stylist:</span> {{ getStylistName(appointment.stylist) }}
            </div>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="appointment.note" class="mt-1">
            <div>
              <v-icon size="small" class="mr-1">mdi-note-text</v-icon>
              <span class="font-weight-medium">Note:</span> {{ appointment.note }}
            </div>
          </v-list-item-subtitle>
          <template #append>
            <v-btn
              variant="tonal"
              color="primary"
              size="small"
              @click="$emit('clone-appointment', appointment)"
              title="Copy appointment"
            >
              <v-icon>mdi-content-copy</v-icon>
              Copy
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
  </div>
</template>

<script setup>
import { useAppointmentStore } from "../../../stores/appointmentsStore";
import { servicesList } from "../../../libs/options";
import { getTitleColor } from "../../../libs/util";
import { format } from "date-fns";

defineProps({
  appointments: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["create-appointment", "clone-appointment"]);

const apptStore = useAppointmentStore();

const formatAppointmentDate = (dateString) => {
  try {
    return format(new Date(dateString), "PPp");
  } catch (e) {
    return dateString;
  }
};

const getServiceNames = (appointment) => {
  // If appointment has services array (from BookingResource), use it directly
  if (appointment.services && Array.isArray(appointment.services)) {
    const names = appointment.services
      .map((service) => service.description)
      .filter((name) => name);
    return names.join(", ") || "N/A";
  }

  // Fallback to service_id if services not available
  let serviceIds = appointment.service_id || appointment;

  // Handle JSON string from database
  if (typeof serviceIds === 'string') {
    try {
      serviceIds = JSON.parse(serviceIds);
    } catch (e) {
      return "N/A";
    }
  }

  if (!Array.isArray(serviceIds)) {
    serviceIds = [serviceIds];
  }

  const names = serviceIds
    .map((id) => {
      const service = servicesList.find((s) => s.id === parseInt(id));
      return service ? service.description : "";
    })
    .filter((name) => name);
  return names.join(", ") || "N/A";
};

const getServiceObjects = (appointment) => {
  // If appointment has services array (from BookingResource), use it directly
  if (appointment.services && Array.isArray(appointment.services)) {
    return appointment.services.filter((service) => service && service.description);
  }

  // Fallback to service_id if services not available
  let serviceIds = appointment.service_id || appointment;

  // Handle JSON string from database
  if (typeof serviceIds === 'string') {
    try {
      serviceIds = JSON.parse(serviceIds);
    } catch (e) {
      return [];
    }
  }

  if (!Array.isArray(serviceIds)) {
    serviceIds = [serviceIds];
  }

  return serviceIds
    .map((id) => {
      const service = servicesList.find((s) => s.id === parseInt(id));
      return service || null;
    })
    .filter((service) => service !== null);
};

const getStylistName = (stylistId) => {
  if (!stylistId) return "N/A";

  // Try to get stylist from the store
  const stylists = apptStore.getStylists;
  if (stylists && stylists.length > 0) {
    const stylist = stylists.find((s) => s.id === parseInt(stylistId));
    if (stylist) return stylist.name;
  }

  // Fallback to showing the ID
  return `Stylist ${stylistId}`;
};
</script>

<style scoped>
.appointment-item {
  border-radius: 8px;
}
</style>
